/** @file treeHandlers: what each `treeBindings` id does, over the store and the tree's visible rows. */
import type { EditorStore } from '../../state/editorTypes';
import { frameNode } from './frameNode';
import { pairMirror } from './pairMirror';
import { removeNode } from './removeNode';
import type { TreeRow } from './treeRows';
import { treeUi } from './treeUi';

/** The tree's own view state the handlers need: the visible rows, the collapse toggle, a yes/no prompt. */
export type TreeView = { rows: () => TreeRow[]; setCollapsed: (name: string, collapsed: boolean) => void; confirm: (text: string) => boolean };

/** Handlers keyed by binding id, for `registerBindings('tree', treeBindings, treeHandlers(store, view))`. */
export function treeHandlers(store: EditorStore, view: TreeView): Record<string, () => void> {
  const current = (): string | undefined => store.getState().selection.nodes.at(-1);
  const ui = () => treeUi(store).getState();
  const step = (by: number) => (): void => {
    const rows = view.rows();
    const at = rows.findIndex((r) => r.name === current());
    const next = rows[Math.min(rows.length - 1, Math.max(0, at < 0 ? 0 : at + by))];
    if (next !== undefined) {
      store.getState().select({ nodes: [next.name], drawable: null });
      ui().setAnchor(next.name);
    }
  };
  const fold = (collapsed: boolean) => (): void => {
    const row = view.rows().find((r) => r.name === current());
    if (row?.hasChildren === true) view.setCollapsed(row.name, collapsed);
  };
  const groups = Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((n) => [`tree.group${n}`, () => { ui().toggleGroup(n); }]));
  return {
    'tree.selectPrev': step(-1),
    'tree.selectNext': step(1),
    'tree.collapse': fold(true),
    'tree.expand': fold(false),
    'tree.rename': () => { const name = current(); if (name !== undefined) ui().startRename(name); },
    'tree.frame': () => { const name = current(); if (name !== undefined) frameNode(store, name); },
    'tree.mirrorPair': () => { pairMirror(store); },
    'tree.remove': () => {
      const name = current();
      if (name !== undefined) removeNode(store, name, view.confirm);
    },
    'tree.viewHide': () => { ui().hideInView(store.getState().selection.nodes); },
    'tree.viewShowAll': () => { ui().showAllInView(); },
    'tree.clearFilters': () => { ui().clearFilters(); },
    ...groups,
  };
}
